import {
  DEFAULT_SPECIAL_BEER_DUTY_KIND,
  createSpecialBeerDuty,
  isSpecialBeerDutyKind,
} from "../../../shared/domain/schedule";
import type {
  BeerDuty,
  ScheduleField,
  ScheduleTimeSlot,
} from "../../../shared/domain/schedule";
import { SCHEDULE_FIELDS } from "../../domain/schedule";
import type {
  PlayoffSeriesInput,
  ScheduleInput,
  SchedulePatch,
} from "../../hooks/teamTypes";
import type { ActiveSchedulePlayer, ScheduleEntry } from "./scheduleTypes";

export interface GameEditorValue {
  scheduledDate: string;
  timeSlot: ScheduleTimeSlot;
  field: ScheduleField;
  opponentName: string;
  isHome: boolean;
  beerDuty: string;
}

export interface SeriesEditorValue {
  title: string;
  opponentName: string;
  games: GameEditorValue[];
}

export interface SeriesMetadataEditorValue {
  id: string;
  title: string;
  opponentName: string;
}

export function currentScheduleDate(now = new Date()): string {
  return now.toISOString().slice(0, 10);
}

export function emptyGameEditor(
  scheduledDate = currentScheduleDate(),
): GameEditorValue {
  return {
    scheduledDate,
    timeSlot: "early",
    field: SCHEDULE_FIELDS[0],
    opponentName: "",
    isHome: false,
    beerDuty: DEFAULT_SPECIAL_BEER_DUTY_KIND,
  };
}

export function emptySeriesEditor(
  scheduledDate = currentScheduleDate(),
): SeriesEditorValue {
  return {
    title: "Playoffs",
    opponentName: "",
    games: Array.from({ length: 3 }, () => emptyGameEditor(scheduledDate)),
  };
}

export function gameEditorFromEntry(entry: ScheduleEntry): GameEditorValue {
  return {
    scheduledDate: entry.scheduledDate,
    timeSlot: entry.timeSlot ?? "early",
    field: (entry.field as ScheduleField | null) ?? SCHEDULE_FIELDS[0],
    opponentName: entry.opponentName,
    isHome: entry.isHome,
    beerDuty:
      entry.beerDuty?.kind === "player"
        ? `player:${entry.beerDuty.playerId}`
        : entry.beerDuty && isSpecialBeerDutyKind(entry.beerDuty.kind)
          ? entry.beerDuty.kind
          : DEFAULT_SPECIAL_BEER_DUTY_KIND,
  };
}

export function beerDutyFromEditor(
  value: string,
  players: ActiveSchedulePlayer[],
): BeerDuty {
  if (isSpecialBeerDutyKind(value)) return createSpecialBeerDuty(value);
  const playerId = value.slice("player:".length);
  return {
    kind: "player",
    playerId,
    nameSnapshot: players.find((player) => player.id === playerId)?.name ?? "",
  };
}

export function scheduleInputFromEditor(
  editor: GameEditorValue,
  players: ActiveSchedulePlayer[],
): ScheduleInput {
  return {
    scheduledDate: editor.scheduledDate,
    timeSlot: editor.timeSlot,
    field: editor.field,
    opponentName: editor.opponentName.trim(),
    isHome: editor.isHome,
    beerDuty: beerDutyFromEditor(editor.beerDuty, players),
  };
}

export function schedulePatchFromEditor(
  editor: GameEditorValue,
  players: ActiveSchedulePlayer[],
  options: { active: boolean; seriesGame: boolean },
): SchedulePatch {
  const schedule = scheduleInputFromEditor(editor, players);
  if (options.active) {
    return {
      scheduledDate: schedule.scheduledDate,
      timeSlot: schedule.timeSlot,
    };
  }
  if (options.seriesGame) {
    return {
      scheduledDate: schedule.scheduledDate,
      timeSlot: schedule.timeSlot,
      field: schedule.field,
      isHome: schedule.isHome,
      beerDuty: schedule.beerDuty,
    };
  }
  return schedule;
}

export function seriesInputFromEditor(
  editor: SeriesEditorValue,
  players: ActiveSchedulePlayer[],
): PlayoffSeriesInput {
  return {
    title: editor.title.trim(),
    opponentName: editor.opponentName.trim(),
    bestOf: 3,
    games: editor.games.map((game) => {
      const schedule = scheduleInputFromEditor(
        { ...game, opponentName: editor.opponentName },
        players,
      );
      return {
        scheduledDate: schedule.scheduledDate,
        timeSlot: schedule.timeSlot,
        field: schedule.field,
        isHome: schedule.isHome,
        beerDuty: schedule.beerDuty,
      };
    }),
  };
}
